/**
 * Remove keys whose value is `undefined`, recursively.
 *
 * In this domain an absent optional field means "not set", and that must look
 * the same in every backend. Firestore rejects `undefined` outright, while a
 * plain object keeps the key — so without normalising, every optional field
 * passes in tests against the in-memory adapter and fails in production.
 *
 * `null` is left alone: it is a deliberate value meaning "explicitly empty",
 * which is how a patch clears a stored field.
 */
export function stripUndefined<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((item) => stripUndefined(item)) as unknown as T;
  }

  // Dates, Maps and the like are values, not plain records: recursing into
  // them would quietly rebuild them as objects.
  if (
    value === null ||
    typeof value !== 'object' ||
    Object.getPrototypeOf(value) !== Object.prototype
  ) {
    return value;
  }

  const result: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    if (item === undefined) continue;
    result[key] = stripUndefined(item);
  }
  return result as T;
}
